/**
 * SelectIcon Component
 *
 * Renders icons from the Sanity icon picker as React components.
 * Some select icons are mapped to Iconoir equivalents for consistency.
 */

import {
  AtSign,
  CardShield,
  CardWallet,
  Wallet,
  Clock,
  CloudCheck,
  Community,
  EmojiSatisfied,
  Globe,
  GraphUp,
  Group,
  Hammer,
  Headset,
  HealthShield,
  HomeSale,
  Laptop,
  LaptopFix,
  LightBulb,
  MessageText,
  MoneySquare,
  OpenBook,
  Packages,
  PeopleTag,
  RefreshCircle,
  Reports,
  Rocket,
  SendDiagonal,
  ShieldCheck,
  Shop,
  ShoppingBagMinus,
  UserBadgeCheck,
  UserSquare,
  WhiteFlag,
} from "iconoir-react";
import type { ComponentType, SVGProps } from "react";

import { cn } from "@/lib/utils";

import type { IconId } from "./generated";
import { ICON_MAP } from "./generated";
import { IconBlackAmazonSquare } from "./icons/black-amazon-square";
import { IconHandTap } from "./icons/hand-tap";
import { IconTransferMoney } from "./icons/transfer-money";
import { IconWayflyerW } from "./icons/wayflyer-w";

export interface SelectIconProps {
  /** The icon ID from Sanity */
  id: string;
  /** Additional CSS classes */
  className?: string;
  /** Alt text for accessibility */
  alt?: string;
}

/** Color definitions for Iconoir icons (matches Figma design) */
const ICONOIR_COLORS = {
  blue: "#5486FA",
  black: "#23232C",
  white: "#FFFFFF",
} as const;

/**
 * Mapping of select icon IDs to Iconoir components with their color.
 * These icons will be rendered as Iconoir icons instead of the original select icons.
 * FIXME: This is a temporary solution to ensure the icons are the correct color.
 * Cleanup this once migration out of Sanity is complete.
 */
const ICONOIR_MAP: Record<
  string,
  { component: ComponentType<SVGProps<SVGSVGElement>>; color: string }
> = {
  // Blue icons (Products page)
  "blue-money-bag": { component: ShoppingBagMinus, color: ICONOIR_COLORS.blue },
  "blue-arrows": { component: RefreshCircle, color: ICONOIR_COLORS.blue },
  "blue-clock": { component: Clock, color: ICONOIR_COLORS.blue },
  "blue-shield": { component: ShieldCheck, color: ICONOIR_COLORS.blue },
  "blue-wave-dollar": { component: MoneySquare, color: ICONOIR_COLORS.blue },

  // Black icons (Solutions submenu - By category)
  "black-cart": { component: Shop, color: ICONOIR_COLORS.black },
  "black-3-packages-alt": { component: HomeSale, color: ICONOIR_COLORS.black },
  "black-storefront-alt": { component: Packages, color: ICONOIR_COLORS.black },
  "black-nav-saas": { component: CloudCheck, color: ICONOIR_COLORS.black },
  "black-nav-app-developer": {
    component: LaptopFix,
    color: ICONOIR_COLORS.black,
  },
  "black-nav-healthcare": {
    component: HealthShield,
    color: ICONOIR_COLORS.black,
  },
  "black-nav-consumer-services": {
    component: Laptop,
    color: ICONOIR_COLORS.black,
  },
  "black-nav-professional-services": {
    component: MessageText,
    color: ICONOIR_COLORS.black,
  },

  // Black icons (Solutions submenu - By stage)
  "black-rocket-launch": { component: Rocket, color: ICONOIR_COLORS.black },
  "black-three-arrows-alt": { component: GraphUp, color: ICONOIR_COLORS.black },
  "black-flag": { component: WhiteFlag, color: ICONOIR_COLORS.black },

  // Black icons (Company submenu)
  "black-list-person": {
    component: IconWayflyerW,
    color: ICONOIR_COLORS.black,
  },
  "black-briefcase": { component: PeopleTag, color: ICONOIR_COLORS.black },
  "black-arrow-right": { component: AtSign, color: ICONOIR_COLORS.black },

  // Black icons (Partners submenu)
  "black-wallet": { component: Wallet, color: ICONOIR_COLORS.black },
  "black-partnerships": { component: UserSquare, color: ICONOIR_COLORS.black },
  "black-people-2": { component: Group, color: ICONOIR_COLORS.black },
  "black-arrows-right-left": {
    component: IconTransferMoney,
    color: ICONOIR_COLORS.black,
  },
  "black-book-open": { component: OpenBook, color: ICONOIR_COLORS.black },
  "black-benchmarks": { component: Reports, color: ICONOIR_COLORS.black },
  "black-headset": { component: Headset, color: ICONOIR_COLORS.black },
  "black-hammer": { component: Hammer, color: ICONOIR_COLORS.black },

  // Blue icons (About Us page - Company History)
  "blue-globe": { component: Globe, color: ICONOIR_COLORS.black },
  "blue-three-arrows": { component: Rocket, color: ICONOIR_COLORS.black },
  "blue-package-base": {
    component: EmojiSatisfied,
    color: ICONOIR_COLORS.black,
  },
  "blue-finger": { component: IconHandTap, color: ICONOIR_COLORS.black },

  // Blue icons (Split Links - Homepage channels)
  "blue-cart": { component: Packages, color: ICONOIR_COLORS.black },
  "blue-package": {
    component: IconBlackAmazonSquare,
    color: ICONOIR_COLORS.black,
  },
  "blue-3-packages-alt": {
    component: SendDiagonal,
    color: ICONOIR_COLORS.black,
  },
  "dark-purple-shield-check": {
    component: ShieldCheck,
    color: ICONOIR_COLORS.white,
  },
  "dark-purple-person-check": {
    component: UserBadgeCheck,
    color: ICONOIR_COLORS.white,
  },
  "dark-purple-funding": {
    component: MoneySquare,
    color: ICONOIR_COLORS.white,
  },
  "dark-purple-chart-bars": {
    component: GraphUp,
    color: ICONOIR_COLORS.white,
  },
  "dark-purple-people-3": {
    component: Community,
    color: ICONOIR_COLORS.white,
  },
  "dark-purple-lightbulb": {
    component: LightBulb,
    color: ICONOIR_COLORS.white,
  },
  "dark-purple-rocket-launch": {
    component: Rocket,
    color: ICONOIR_COLORS.white,
  },
  "dark-purple-person-square": {
    component: UserSquare,
    color: ICONOIR_COLORS.white,
  },
  "dark-purple-moneybag-dollar": {
    component: MoneySquare,
    color: ICONOIR_COLORS.white,
  },
  "dark-purple-payment-card-check": {
    component: CardShield,
    color: ICONOIR_COLORS.white,
  },
  "dark-purple-hand-banknote": {
    component: CardWallet,
    color: ICONOIR_COLORS.white,
  },
};

/**
 * Renders an icon from the Sanity icon picker.
 * Some icons are mapped to Iconoir equivalents for visual consistency.
 */
export function SelectIcon({ id, className, alt }: SelectIconProps) {
  // Check if this icon should be rendered as an Iconoir icon
  const iconoirMapping = ICONOIR_MAP[id];
  if (iconoirMapping) {
    const { component: IconoirComponent, color } = iconoirMapping;
    return (
      <IconoirComponent
        className={cn("inline-block shrink-0 align-middle", className)}
        aria-label={alt || id}
        role={alt ? "img" : "presentation"}
        color={color}
      />
    );
  }

  // Fall back to the original select icon
  const IconComponent = ICON_MAP[id as IconId];

  if (!IconComponent) {
    // eslint-disable-next-line no-console
    console.warn(`SelectIcon: Unknown icon ID "${id}"`);
    return null;
  }

  return (
    <IconComponent
      className={cn("inline-block shrink-0 align-middle", className)}
      aria-label={alt || id}
      role={alt ? "img" : "presentation"}
    />
  );
}
